import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ProfileContext } from "../contexts/ProfileContext";
import { LoadingSpinner } from "../components/loading-spinner";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import OrganizationNameButton from "../components/organization-name-button";

const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const AgeGender = () => {
  // ========== COMMON STATE ==========
  const [dataType, setDataType] = useState("ytd");
  const [exportOption, setExportOption] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [selectedRange, setSelectedRange] = useState({ start: "", end: "" });
  const [isLoading, setIsLoading] = useState(false);

  // ========== PAGE-SPECIFIC STATE ==========
  const [donutData, setDonutData] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(true);

  // ========== REFS ==========
  const selectRef = useRef(null);
  const datePickerRef = useRef(null);
  const reportRef = useRef();

  // ========== CONTEXT ==========
  const {
    getAgeGenderReport,
    organizationNumber,
    organization,
    worker,
    organizationFirebaseId,
  } = useContext(ProfileContext);

  // ========== DATE HELPERS ==========
  const formatDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;

  const getYTDRange = () => {
    const now = new Date();
    const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
    return { start: formatDate(firstDayOfYear), end: formatDate(now) };
  };

  const getDefaultMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(firstDay), end: formatDate(now) };
  };

  // ========== DATA FETCHING ==========
  const fetchAgeGenderData = async (
    type = "",
    startDate = "",
    endDate = "",
  ) => {
    try {
      setIsLoading(true);

      // Check if we have Firebase ID
      if (!organizationFirebaseId) {
        console.error("❌ No Firebase ID available for organization");
        setDonutData([]);
        setLineData([]);
        return;
      }

      let result;
      let finalStart, finalEnd;

      if (type === "all") {
        finalStart = "2015-01-01";
        finalEnd = formatDate(new Date());
        result = await getAgeGenderReport(
          organizationFirebaseId,
          "full",
          finalStart,
          finalEnd,
        );
        setSelectedRange({ start: finalStart, end: finalEnd });
      } else if (type === "ytd") {
        const ytd = getYTDRange();
        finalStart = ytd.start;
        finalEnd = ytd.end;
        result = await getAgeGenderReport(
          organizationFirebaseId,
          "full",
          finalStart,
          finalEnd,
        );
        setSelectedRange(ytd);
      } else if (type === "monthly") {
        const def = getDefaultMonthRange();
        finalStart = def.start;
        finalEnd = def.end;
        result = await getAgeGenderReport(
          organizationFirebaseId,
          "full",
          finalStart,
          finalEnd,
        );
        setSelectedRange(def);
      } else if (type === "custom") {
        if (!startDate || !endDate) {
          console.warn("Custom date range selected but no dates provided");
          return;
        }
        finalStart = startDate;
        finalEnd = endDate;
        result = await getAgeGenderReport(
          organizationFirebaseId,
          "full",
          finalStart,
          finalEnd,
        );
        setSelectedRange({ start: startDate, end: endDate });
      } else {
        // Default case - fetch with default dates (current month)
        const def = getDefaultMonthRange();
        finalStart = def.start;
        finalEnd = def.end;
        result = await getAgeGenderReport(
          organizationFirebaseId,
          "full",
          finalStart,
          finalEnd,
        );
        setSelectedRange({ start: "", end: "" });
      }

      console.log("📊 Age/Gender API Response:", result);

      // Process the response - NEW STRUCTURE
      let processedData;

      // Check different possible response structures
      if (result?.original) {
        // Structure: { original: { gender: {...}, age: {...} } }
        processedData = result.original;
      } else if (result?.gender || result?.age) {
        // Structure: { gender: {...}, age: {...} } (NEW STRUCTURE)
        processedData = result;
      } else {
        processedData = {};
      }

      if (!processedData || Object.keys(processedData).length === 0) {
        setDonutData([]);
        setLineData([]);
        return;
      }

      // Map gender colors
      const genderColors = {
        male: "#EC3310",
        female: "#FF7234",
        "prefer not to say": "#FCD5C9",
        "prefer not to answer": "#FCD5C9",
        "prefer not to tell": "#FCD5C9",
        other: "#7F7F7F",
        unspecified: "#7F7F7F",
      };

      // Process gender data - NEW STRUCTURE: gender is an object
      let genderReportData = [];

      if (processedData.gender && typeof processedData.gender === "object") {
        // Convert object to array format for charts
        genderReportData = Object.entries(processedData.gender).map(
          ([key, value]) => {
            const gender = key;
            const count = value;

            return {
              name: gender,
              value: Number(count),
              color: genderColors[gender.toLowerCase()] || "#7F7F7F",
            };
          },
        );
      }

      // Process age data - NEW STRUCTURE: age is an object
      let ageReportData = [];

      if (processedData.age && typeof processedData.age === "object") {
        // Convert object to array format for charts
        ageReportData = Object.entries(processedData.age).map(
          ([key, value]) => {
            return {
              name: key,
              value: Number(value),
            };
          },
        );
      }

      setDonutData(genderReportData);
      setLineData(ageReportData);
    } catch (error) {
      console.error("❌ Error fetching age/gender report:", error);
      setDonutData([]);
      setLineData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== INITIAL FETCH ==========
  useEffect(() => {
    // Only fetch if we have Firebase ID
    if (organizationFirebaseId) {
      fetchAgeGenderData("ytd");
    } else {
      console.log("⏳ Waiting for Firebase ID...");
    }
  }, [organizationFirebaseId]);

  // ========== FILTER HANDLERS ==========
  const handleFilterChange = (type) => {
    setDataType(type);
    if (type === "custom") {
      setShowDatePicker(true);
    } else {
      fetchAgeGenderData(type);
    }
  };

  const handleApplyCustomDates = () => {
    if (!tempStartDate || !tempEndDate) {
      alert("Select both dates.");
      return;
    }
    setDataType("custom");
    setShowDatePicker(false);
    fetchAgeGenderData("custom", tempStartDate, tempEndDate);
  };

  const handleCancelCustomDates = () => {
    setTempStartDate("");
    setTempEndDate("");
    setShowDatePicker(false);
  };

  // ========== EXPORT FUNCTIONALITY ==========
  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
    documentTitle: `Age_gender_${selectedRange.start || "all"}_to_${
      selectedRange.end || "all"
    }`,
    onBeforePrint: () => {
      document.body.style.width = "100%";
    },
    onAfterPrint: () => {
      setExportOption("");
      document.body.style.width = "";
    },
  });

  const handleExportExcel = () => {
    const genderSheetData = donutData.length
      ? donutData.map((d) => ({
          Category: "Gender",
          Label: d.name,
          Count: d.value,
        }))
      : [{ Category: "Gender", Label: "No Data", Count: 0 }];

    const ageSheetData = lineData.length
      ? lineData.map((d) => ({
          Category: "Age",
          Label: d.name,
          Count: d.value,
        }))
      : [{ Category: "Age", Label: "No Data", Count: 0 }];

    // Add total rows to Excel export
    const genderTotal = donutData.reduce((sum, item) => sum + item.value, 0);
    const ageTotal = lineData.reduce((sum, item) => sum + item.value, 0);

    genderSheetData.push({
      Category: "Gender",
      Label: "TOTAL",
      Count: genderTotal,
    });
    ageSheetData.push({ Category: "Age", Label: "TOTAL", Count: ageTotal });

    const worksheet = XLSX.utils.json_to_sheet([
      ...genderSheetData,
      ...ageSheetData,
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Age_gender");

    const fileName = `Age_gender_${selectedRange.start || "all"}_to_${
      selectedRange.end || "all"
    }.xlsx`;
    XLSX.writeFile(workbook, fileName);
    setExportOption("");
  };

  useEffect(() => {
    if (exportOption === "print") handlePrint();
    if (exportOption === "export") handleExportExcel();
  }, [exportOption]);

  // ========== UI HANDLERS ==========
  const toggleBreakdown = () => {
    setIsBreakdownOpen(!isBreakdownOpen);
  };

  // ========== SELECT & DATEPICKER EFFECTS ==========
  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;

    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    const handleBlur = () => setIsSelectOpen(false);

    selectElement.addEventListener("mousedown", handleMouseDown);
    selectElement.addEventListener("blur", handleBlur);

    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker]);

  // ========== CALCULATIONS ==========
  const genderTotal = donutData.reduce((sum, item) => sum + item.value, 0);
  const ageTotal = lineData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Wrapper>
      {/* ========== STANDARDIZED TOP SECTION ========== */}
      <TopSection>
        {(worker && worker.role === "subscriber") ||
        organization?.name?.toLowerCase() === "crossroads" ? null : (
          <OrganizationNameButton />
        )}

        <TitleText>Age + Gender of Callers</TitleText>
      </TopSection>

      {/* ========== STANDARDIZED TOGGLE FILTERS ========== */}
      <ToggleButtonContainer>
        <ToggleContent>
          <ToggleButtonGroup>
            <ToggleButton
              active={dataType === "ytd"}
              onClick={() => handleFilterChange("ytd")}
            >
              YEAR TO DATE
            </ToggleButton>

            <ToggleButton
              active={dataType === "all"}
              onClick={() => handleFilterChange("all")}
            >
              ALL DATA
            </ToggleButton>

            <ToggleButton
              active={dataType === "monthly"}
              onClick={() => handleFilterChange("monthly")}
            >
              CURRENT MONTH
            </ToggleButton>

            <ToggleButton
              active={dataType === "custom"}
              onClick={() => {
                setDataType("custom");
                setShowDatePicker(true);
              }}
            >
              CUSTOM DATE RANGE
            </ToggleButton>
          </ToggleButtonGroup>

          <ExportContainer>
            <SelectWrapper>
              <ExportSelect
                ref={selectRef}
                value={exportOption}
                onChange={(e) => setExportOption(e.target.value)}
              >
                <option value="" disabled>
                  Export Options
                </option>
                <option value="print">Print PDF</option>
                <option value="export">Export as Excel Sheet</option>
              </ExportSelect>
              <ArrowIcon isOpen={isSelectOpen}>
                <ChevronDown />
              </ArrowIcon>
            </SelectWrapper>
          </ExportContainer>
        </ToggleContent>

        {showDatePicker && (
          <DatePickerDropdown ref={datePickerRef}>
            <DateInputGroup>
              <label>From</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </DateInputGroup>

            <DateInputGroup>
              <label>To</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </DateInputGroup>

            <DatePickerActions>
              <button onClick={handleApplyCustomDates}>Apply</button>
              <button onClick={handleCancelCustomDates}>Cancel</button>
            </DatePickerActions>
          </DatePickerDropdown>
        )}
      </ToggleButtonContainer>

      {/* ========== PAGE-SPECIFIC CONTENT ========== */}
      <printContainer ref={reportRef}>
        {/* ========== HIDDEN PDF-ONLY TITLE ========== */}
        <PdfOnlyHeader>
          <div className="main-title">AGE + GENDER OF CALLERS</div>
        </PdfOnlyHeader>
        <ChartsContainer>
          <DonutCard>
            {isLoading ? (
              <SpinnerWrapper>
                <LoadingSpinner />
              </SpinnerWrapper>
            ) : (
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={
                        donutData.length
                          ? donutData
                          : [{ name: "No Data", value: 1, color: "#E0E0E0" }]
                      }
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="90%"
                      paddingAngle={0}
                      dataKey="value"
                    >
                      {(donutData.length
                        ? donutData
                        : [{ color: "#E0E0E0" }]
                      ).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    {donutData.length > 0 && <Tooltip />}
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </DonutCard>

          {/* 🟣 Line Chart */}
          <LineCard>
            {isLoading ? (
              <SpinnerWrapper>
                <LoadingSpinner />
              </SpinnerWrapper>
            ) : (
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={lineData}
                    margin={{ top: 20, right: 10, left: -20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="name"
                      angle={-20}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 11 }}
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#9A89FF"
                      strokeWidth={2}
                      dot={{ fill: "#B1A5FF", r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </LineCard>
        </ChartsContainer>

        {/* 🟡 Tables */}
        <BreakdownCard isOpen={isBreakdownOpen}>
          <BreakdownTitle onClick={toggleBreakdown}>
            <ChevronIcon>
              {isBreakdownOpen ? <ChevronDown /> : <ChevronRight />}
            </ChevronIcon>
            AGE + GENDER BREAKDOWN
          </BreakdownTitle>

          {isBreakdownOpen && (
            <BreakdownContent>
              <BreakdownTables>
                <TableContainer>
                  <TableTitle>Gender Distribution</TableTitle>
                  {isLoading ? (
                    <SpinnerWrapper>
                      <LoadingSpinner />
                    </SpinnerWrapper>
                  ) : (
                    <StyledTable>
                      <thead>
                        <tr>
                          <th>Color</th>
                          <th>Gender</th>
                          <th>Total Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donutData.length ? (
                          <>
                            {donutData.map((item, index) => (
                              <tr key={index}>
                                <td>
                                  <TableColorBox color={item.color} />
                                </td>
                                <td>{item.name}</td>
                                <td>{item.value}</td>
                              </tr>
                            ))}
                            {/* ✅ Total Row for Gender Table */}
                            <tr className="total-row">
                              <td
                                colSpan="2"
                                style={{
                                  textAlign: "left",
                                  fontWeight: "bold",
                                }}
                              >
                                Total
                              </td>
                              <td style={{ fontWeight: "bold" }}>
                                {genderTotal}
                              </td>
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan="3">No gender data available.</td>
                          </tr>
                        )}
                      </tbody>
                    </StyledTable>
                  )}
                </TableContainer>

                <TableContainer>
                  <TableTitle>Age Distribution</TableTitle>
                  {isLoading ? (
                    <SpinnerWrapper>
                      <LoadingSpinner />
                    </SpinnerWrapper>
                  ) : (
                    <StyledTable>
                      <thead>
                        <tr>
                          <th>Age Range</th>
                          <th>Total Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineData.length ? (
                          <>
                            {lineData.map((item, index) => (
                              <tr key={index}>
                                <td>{item.name}</td>
                                <td>{item.value}</td>
                              </tr>
                            ))}
                            {/* ✅ Total Row for Age Table */}
                            <tr className="total-row">
                              <td
                                style={{
                                  textAlign: "left",
                                  fontWeight: "bold",
                                }}
                              >
                                Total
                              </td>
                              <td style={{ fontWeight: "bold" }}>{ageTotal}</td>
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan="2">No age data available.</td>
                          </tr>
                        )}
                      </tbody>
                    </StyledTable>
                  )}
                </TableContainer>
              </BreakdownTables>
            </BreakdownContent>
          )}
        </BreakdownCard>
      </printContainer>
    </Wrapper>
  );
};

export default AgeGender;

// ========== STYLED COMPONENTS ==========

// Standardized components from the example

const PdfOnlyHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin: 40px 0;
    padding-bottom: 20px;
    font-family: "Helvetica", "Arial", sans-serif;

    .main-title {
      text-align: center;
      font-family: Recoleta, serif;
      font-size: 1.25rem;
      font-weight: bold;
      color: #ec3310;
      margin-bottom: 0;
      text-decoration: underline;
      grid-column: 2;

      @media (max-width: 768px) {
        grid-column: 1;
        font-size: 1.1rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const ToggleButtonContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 1rem;
  }
`;

const ToggleContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  background: #eeeeee;
  border: 1px solid #e5e7eb;
  border-radius: 62px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 12px;
    max-width: 300px;
    width: 100%;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ExportContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  width: auto;
  min-width: 164px;
  height: 29px;
  padding: 0 12px;

  font-family: "Futura PT", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.15em;
  text-align: center; /* Center alignment */

  border: none;
  border-radius: 62px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${({ active }) => (active ? "#2DB3C0" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#86CCDA")};
  box-shadow: ${({ active }) =>
    active ? "0 2px 6px rgba(0, 0, 0, 0.15)" : "none"};

  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    height: 40px;

    font-size: 14px; /* Keep typography consistent */
    letter-spacing: 0.15em; /* Keep consistent */
    border-radius: 8px;
    margin: 2px 0;
  }

  @media (max-width: 480px) {
    height: 36px;

    font-size: 14px; /* Maintain required size */
    letter-spacing: 0.25em; /* Maintain required spacing */

    padding: 0 8px;
  }
`;

const DatePickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 280px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 480px) {
    min-width: 260px;
    padding: 12px;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;

  label {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    font-family: "Space Grotesk";
  }

  input {
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    font-size: 13px;
    font-family: "Space Grotesk";
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #2db3c0;
      box-shadow: 0 0 0 2px rgba(45, 179, 192, 0.1);
    }
  }
`;

const DatePickerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;

  button {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-family: "Space Grotesk";
    font-weight: 600;
    border: none;
    transition: all 0.2s;

    &:first-child {
      background-color: #2db3c0;
      color: white;

      &:hover {
        background-color: #249aa5;
      }
    }

    &:last-child {
      background-color: #f3f4f6;
      color: #666;

      &:hover {
        background-color: #e5e7eb;
      }
    }
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 172px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 200px;
  }
`;

const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk";
  border-radius: 8px;
  width: 100%;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 10px;

  &:focus {
    outline: none;
  }
`;

const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.3s ease;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const TitleText = styled.h2`
  text-align: center;
  font-family: Recoleta, serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ec3310;
  margin-bottom: 0;
  text-decoration: underline;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const printContainer = styled.div`
  width: 100%;
  display: block;
  @media print {
    width: 100%;
    display: block;
  }
`;

// Your existing styled components (keep as is)
const Wrapper = styled.div`
  width: 100%;
  padding: 1rem 0;
  background-color: #f3f9fa;
  min-height: 100vh;
  overflow-x: hidden;

  @media (max-width: 480px) {
    padding: 0.5rem 0;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const DonutCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  width: 100%;

  @media (max-width: 768px) {
    min-height: 350px;
    padding: 0.5rem;
  }

  @media (max-width: 480px) {
    min-height: 300px;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;

  @media (max-width: 768px) {
    height: 250px;
  }

  @media (max-width: 480px) {
    height: 220px;
  }
`;

const LineCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1rem;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  @media (max-width: 768px) {
    min-height: 350px;
    padding: 0.5rem;
  }

  @media (max-width: 480px) {
    min-height: 300px;
  }
`;

const BreakdownCard = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #dee2e6;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  padding: ${({ isOpen }) => (isOpen ? "1.5rem" : "1rem 1.5rem")};
  font-family: "Space Grotesk", sans-serif;
  overflow: hidden;
  transition: all 0.3s ease;

  @media (max-width: 480px) {
    border-radius: 12px;
    padding: ${({ isOpen }) => (isOpen ? "1rem" : "0.75rem 1rem")};
    margin-top: 1.5rem;
  }
`;

const BreakdownTitle = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 700; /* Bold */
  font-style: normal;
  line-height: 100%;
  color: #2db3c0;
  letter-spacing: 0.25em; /* 25% spacing */
  text-align: center;

  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #249aa5;
  }

  @media (max-width: 480px) {
    font-size: 14px; /* Keeping typography consistent */
    letter-spacing: 0.25em; /* Keeping consistent */
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BreakdownContent = styled.div`
  margin-top: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const BreakdownTables = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  min-width: 280px;
  overflow: hidden;

  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

const TableTitle = styled.h5`
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.8rem;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 14px;
    text-align: left;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  table-layout: fixed;
  font-family: "Space Grotesk", sans-serif;
  background-color: #fff;

  th,
  td {
    padding: 12px 8px;
    border-bottom: 1px solid #dee2e6;
    word-wrap: break-word;
  }

  thead th {
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f2f6f7;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  /* ✅ Total row styling */
  .total-row {
    background-color: #f2f6f7;
    font-weight: bold;
  }

  @media (max-width: 480px) {
    font-size: 12px;

    th,
    td {
      padding: 8px 4px;
    }

    thead th {
      font-size: 12px;
    }
  }

  @media (max-width: 360px) {
    font-size: 11px;

    th,
    td {
      padding: 6px 3px;
    }
  }
`;

const TableColorBox = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${({ color }) => color};
  border-radius: 4px;
  border: 1px solid #ddd;

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
  }
`;
